import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { clearYouTubeProgress } from '../components/youtubeProgress';
import { useEffect, useState } from 'react';

export const PLAYBACK_EVENT = 'podeux:episode-playback';
const key = uid => `episode-playback-v1:${uid || 'guest'}`;
const samples = new Map();
const inFlight = new Map();
const deleting = new Set();
const drainRequests = new Map();
const activityKey = (uid, id) => `${uid || 'guest'}:${id}`;
const maxSeconds = 2147483647;
const validTime = value => Number.isFinite(value) && value >= 0 && value <= maxSeconds;
const validTimestamp = value => Number.isFinite(value) && value > 0;
const validVideoId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{11}$/.test(value);

export const validProgress = progress => {
    if (!progress || !validTime(progress.seconds) || !validTimestamp(progress.updatedAtSeconds)) return false;
    if (progress.durationSeconds != null && (!validTime(progress.durationSeconds) ||
        progress.durationSeconds <= 0 || progress.seconds > progress.durationSeconds + 1)) return false;
    if (progress.actualPlayedAtSeconds != null && (!validTimestamp(progress.actualPlayedAtSeconds) ||
        progress.actualPlayedAtSeconds > progress.updatedAtSeconds)) return false;
    return progress.videoId == null || validVideoId(progress.videoId);
};

export const finishedProgress = progress => {
    if (progress?.completed === true) return true;
    const { seconds, durationSeconds } = progress || {};
    if (!validTime(seconds) || !validTime(durationSeconds) || durationSeconds <= 0) return false;
    const remaining = durationSeconds - seconds;
    return remaining < 20 && remaining / durationSeconds < 0.01;
};

export const resumeProgress = progress => finishedProgress(progress) ? 0 : progress?.seconds || 0;

const read = uid => {
    try {
        const records = JSON.parse(localStorage.getItem(key(uid)));
        return records && typeof records === 'object' && !Array.isArray(records) ? records : {};
    } catch {
        return {};
    }
};

const write = (uid, records) => {
    try {
        localStorage.setItem(key(uid), JSON.stringify(records));
    } catch {
        // Playback remains available if storage is blocked or full.
    }
    window.dispatchEvent(new Event(PLAYBACK_EVENT));
};

const mergeProgress = (existing, incoming, video = false) => {
    const accepts = progress => validProgress(progress) && (!video || validVideoId(progress.videoId));
    const previous = accepts(existing) ? existing : undefined;
    if (!accepts(incoming)) return previous;
    if (!previous) return incoming;

    const newest = incoming.updatedAtSeconds >= previous.updatedAtSeconds ? incoming : previous;
    const result = { ...newest };
    // Position and actual playback are separate clocks. A late checkpoint must
    // not erase activity, or inherit activity from a replacement video asset.
    if (!video || previous.videoId === incoming.videoId) {
        result.actualPlayedAtSeconds = Math.max(previous.actualPlayedAtSeconds || 0,
            incoming.actualPlayedAtSeconds || 0) || undefined;
    }
    return result;
};

export const playbackRecord = (uid, track) => {
    const local = read(uid)[track?.id] || {};
    const server = track || {};
    if (local.deletedAt && !(server.lastPlayedAtSeconds > local.deletedAt)) return local;
    const activity = (local.lastPlayedAtSeconds || 0) >= (server.lastPlayedAtSeconds || 0) ? local : server;
    return {
        ...local,
        deletedAt: undefined,
        audioProgress: mergeProgress(server.audioProgress, local.audioProgress),
        videoProgress: mergeProgress(server.videoProgress, local.videoProgress, true),
        lastPlayedFormat: activity.lastPlayedFormat === 'video' ? 'video' : 'audio',
        lastPlayedAtSeconds: activity.lastPlayedAtSeconds,
    };
};
export const useEpisodePlayback = (uid, track) => {
    const [, refresh] = useState(0);
    useEffect(() => {
        const update = () => refresh(value => value + 1);
        window.addEventListener(PLAYBACK_EVENT, update);
        window.addEventListener('storage', update);
        return () => {
            window.removeEventListener(PLAYBACK_EVENT, update);
            window.removeEventListener('storage', update);
        };
    }, []);
    return playbackRecord(uid, track);
};
// The authenticated caller supplies an account check that stays valid after an await.
export const flushPlayback = (uid, isCurrent) => {
    if (!uid || !isCurrent()) return Promise.resolve();
    const requestKey = key(uid);
    if (inFlight.has(requestKey)) {
        drainRequests.set(requestKey, isCurrent);
        return inFlight.get(requestKey);
    }
    const request = (async () => {
        let succeeded = false;
        try {
            const { validTokenExists } = await import('../firebaseHelper');
            await validTokenExists();
            if (!isCurrent()) return;
            const token = localStorage.getItem('FirebaseIdToken');
            const tokenUser = jwtDecode(token?.replace(/^Bearer /, '') || '').sub;
            if (tokenUser !== uid) return;
            const current = () => isCurrent() && localStorage.getItem('FirebaseIdToken') === token;
            const records = read(uid);
            for (const [trackId, record] of Object.entries(records)) {
                for (const format of ['audio', 'video']) {
                    const pending = record.pending?.[format];
                    if (!pending || !current() || deleting.has(activityKey(uid, trackId)) || read(uid)[trackId]?.deletedAt) continue;
                    const response = await axios.post('/last_listen', { trackId, format, ...pending }, {
                        headers: { Authorization: token },
                    });
                    if (response.data?.success !== true) throw new Error('Playback save failed');
                    if (!isCurrent()) return;
                    const latest = read(uid);
                    if (latest[trackId]?.pending?.[format]?.clientUpdatedAtSeconds === pending.clientUpdatedAtSeconds) {
                        delete latest[trackId].pending[format];
                        write(uid, latest);
                    }
                }
            }
            succeeded = true;
        } catch {
            // Keep failed checkpoints for the next playback or reconnect.
        } finally {
            inFlight.delete(requestKey);
            const requested = drainRequests.get(requestKey);
            drainRequests.delete(requestKey);
            // A new login can be waiting on this account's retired request.
            // Retry that handoff once; ordinary failures still wait for a new trigger.
            if (requested?.() && (succeeded || !isCurrent())) {
                Promise.resolve().then(() => flushPlayback(uid, requested));
            }
        }
    })();
    inFlight.set(requestKey, request);
    return request;
};
export const beginPlaybackDeletion = async (uid, ids) => {
    ids.forEach(id => deleting.add(activityKey(uid, id)));
    await inFlight.get(key(uid));
    return success => {
        if (success) removePlayback(uid, ids);
        ids.forEach(id => deleting.delete(activityKey(uid, id)));
    };
};
export const hydratePlayback = (uid, incoming) => {
    if (!uid || !incoming) return;
    const records = read(uid);
    Object.entries(incoming).forEach(([id, value]) => {
        records[id] = playbackRecord(uid, { id, ...value });
        if (value.source && !records[id].deletedAt) records[id].trackInfo = { ...value, id };
    });
    write(uid, records);
};
export const recordPlayback = (uid, track, format, { seconds, durationSeconds, videoId, actualPlayedAtSeconds, completed = false, resetsCompletion = false }, now = Date.now() / 1000) => {
    if (!['audio', 'video'].includes(format) || !validTimestamp(now) || !track?.id || deleting.has(activityKey(uid, track.id)) || !validTime(seconds) || !validTime(durationSeconds) || durationSeconds <= 0 || seconds > durationSeconds + 1 ||
        (format === 'video' && !validVideoId(videoId))) return false;
    const records = read(uid);
    const record = playbackRecord(uid, track);
    const previous = record[`${format}Progress`];
    if (record.deletedAt && !(actualPlayedAtSeconds > record.deletedAt)) return false;
    if (actualPlayedAtSeconds != null && (!validTimestamp(actualPlayedAtSeconds) || actualPlayedAtSeconds > now)) return false;
    if (!actualPlayedAtSeconds && !previous && !record.lastPlayedAtSeconds) return false;
    if ((previous?.updatedAtSeconds || 0) > now) return false;
    const sameAsset = format !== 'video' || previous?.videoId === videoId;
    // Teardown and paused checkpoints cannot undo natural completion. Explicit
    // replay/seek intents can reset it before the next real playback event.
    const stillAtCompletedEnd = sameAsset && previous?.completed && Math.abs(seconds - durationSeconds) <= 1;
    const isCompleted = completed || (stillAtCompletedEnd && !actualPlayedAtSeconds && !resetsCompletion) || false;
    const progress = { seconds: Math.min(seconds, durationSeconds), durationSeconds, completed: isCompleted,
        updatedAtSeconds: now, actualPlayedAtSeconds: Math.max(actualPlayedAtSeconds || 0, sameAsset ? previous?.actualPlayedAtSeconds || 0 : 0) || undefined,
        ...(format === 'video' ? { videoId } : {}) };
    const sampleKey = `${uid || 'guest'}:${track.id}:${format}:${videoId || ''}`;
    if (actualPlayedAtSeconds) {
        const sample = samples.get(sampleKey);
        const elapsed = now - (sample?.time || 0);
        const delta = seconds - (sample?.seconds || 0);
        if (sample && elapsed > 0 && elapsed <= 10 && delta > 0 && delta <= Math.max(3, elapsed * 4)) {
            record.played = { ...record.played, [format]: (record.played?.[format] || 0) + delta };
        }
        samples.set(sampleKey, { time: now, seconds });
        if (actualPlayedAtSeconds >= (record.lastPlayedAtSeconds || 0)) {
            record.lastPlayedFormat = format;
            record.lastPlayedAtSeconds = actualPlayedAtSeconds;
        }
        delete record.deletedAt;
    } else samples.delete(sampleKey);
    record[`${format}Progress`] = progress;
    record.trackInfo = { ...track, transcript: undefined, description: undefined };
    const unsent = sameAsset ? record.pending?.[format]?.actualPlayedAtSeconds : undefined;
    record.pending = { ...record.pending, [format]: { seconds: progress.seconds, durationSeconds, completed: progress.completed,
        clientUpdatedAtSeconds: now, ...(videoId ? { videoId } : {}),
        ...((actualPlayedAtSeconds || unsent) ? { actualPlayedAtSeconds: Math.max(actualPlayedAtSeconds || 0, unsent || 0) } : {}) } };
    records[track.id] = record;
    write(uid, records);
    return true;
};
const continuationEstimate = (source, target, targetDuration) => {
    if (!validProgress(source) || finishedProgress(source) || source.seconds <= 0 ||
        !(source.actualPlayedAtSeconds > (target?.actualPlayedAtSeconds || 0)) ||
        !validTime(source.durationSeconds) || source.durationSeconds <= 0 ||
        !validTime(targetDuration) || targetDuration <= 0) return null;

    const fraction = Math.min(1, Math.max(0, source.seconds / source.durationSeconds));
    const estimate = Math.floor(Math.min(Math.max(0, fraction * targetDuration - 15), Math.max(0, targetDuration - 1)));
    return Math.abs(estimate - resumeProgress(target)) >= 30 ? estimate : null;
};

const hasNewPlayback = (record, format, consumed) => consumed == null ||
    (record.played?.[format] || 0) - consumed >= 10;

export const audioContinuation = (record, videoId, duration) => {
    const source = record.videoProgress;
    if (!validVideoId(videoId) || source?.videoId !== videoId) return null;
    const dismissed = record.dismissed?.videoId === videoId ? record.dismissed.played : null;
    if (!hasNewPlayback(record, 'video', dismissed) ||
        !hasNewPlayback(record, 'video', record.accepted?.video)) return null;
    return continuationEstimate(source, record.audioProgress, duration);
};

export const videoContinuation = (record, videoId) => {
    const target = record.videoProgress;
    if (!validVideoId(videoId) || target?.videoId !== videoId || !validProgress(target) ||
        !hasNewPlayback(record, 'audio', record.accepted?.audio)) return null;
    return continuationEstimate(record.audioProgress, target, target.durationSeconds);
};

export const consumeContinuation = (uid, track, videoId, accepted = false) => {
    const records = read(uid);
    const record = playbackRecord(uid, track);
    if (accepted) record.accepted = { audio: record.played?.audio || 0, video: record.played?.video || 0 };
    else record.dismissed = { videoId, played: record.played?.video || 0 };
    records[track.id] = record;
    write(uid, records);
};
export const localPlayedTracks = uid => Object.entries(read(uid)).filter(([, r]) => !r.deletedAt && r.lastPlayedAtSeconds && r.trackInfo)
    .map(([id, r]) => ({ ...r.trackInfo, ...playbackRecord(uid, { ...r.trackInfo, id }) }));
export const mergePlayedTracks = (uid, ...groups) => {
    const tracks = new Map();
    groups.flat().forEach(track => {
        if (!track?.id) return;
        const previous = tracks.get(track.id) || {};
        const activity = (track.lastPlayedAtSeconds || 0) >= (previous.lastPlayedAtSeconds || 0) ? track : previous;
        const metadata = { ...previous, ...track,
            audioProgress: mergeProgress(previous.audioProgress, track.audioProgress),
            videoProgress: mergeProgress(previous.videoProgress, track.videoProgress, true),
            lastPlayedAtSeconds: activity.lastPlayedAtSeconds, lastPlayedFormat: activity.lastPlayedFormat };
        const history = playbackRecord(uid, metadata);
        if (!history.deletedAt) tracks.set(track.id, { ...metadata, ...history });
    });
    return [...tracks.values()].sort((a, b) => (b.lastPlayedAtSeconds || 0) - (a.lastPlayedAtSeconds || 0));
};
export const removePlayback = (uid, ids) => {
    const records = read(uid);
    ids.forEach(id => {
        const videoId = records[id]?.videoProgress?.videoId;
        if (videoId) clearYouTubeProgress(uid, videoId);
        for (const sample of samples.keys()) {
            if (sample.startsWith(`${activityKey(uid, id)}:`)) samples.delete(sample);
        }
        records[id] = { deletedAt: Date.now() / 1000 };
    });
    write(uid, records);
};


// Pending writes outlive individual player screens. Retry after account
// restoration and reconnect even when no episode is currently open.
export const installPlaybackSync = store => {
    let active = true;
    let previousUid;
    let previousRevision;
    let previousAuthenticated;
    let generation = 0;
    const currentAccount = () => {
        const user = store.getState().user;
        return {
            uid: user.authenticated && user.uid ? user.uid : null,
            revision: user.forMeSessionRevision,
            authenticated: user.authenticated,
        };
    };
    const sync = () => {
        const { uid, revision } = currentAccount();
        const requestGeneration = generation;
        if (uid) flushPlayback(uid, () => {
            const current = currentAccount();
            return active && requestGeneration === generation &&
                current.uid === uid && current.revision === revision;
        });
    };
    const accountChanged = () => {
        const { uid, revision, authenticated } = currentAccount();
        if (uid === previousUid && revision === previousRevision && authenticated === previousAuthenticated) return;
        previousUid = uid;
        previousRevision = revision;
        previousAuthenticated = authenticated;
        generation += 1;
        samples.clear();
        sync();
    };
    const unsubscribe = store.subscribe(accountChanged);
    window.addEventListener('online', sync);
    accountChanged();
    return () => {
        active = false;
        unsubscribe();
        window.removeEventListener('online', sync);
    };
};
